import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import {
  MI_SEARCHES_TYPE_FLIGHTS_BUSINESS,
  MI_SEARCHES_TYPE_FLIGHTS_LEISURE,
  MI_SEARCHES_TYPE_HOTELS_BUSINESS,
  MI_SEARCHES_TYPE_HOTELS_LEISURE,
} from 'lumen/utils/constants/general-constants';
import { FIXED_EXTRACT_DATE_LATEST } from 'lumen/utils/constants/market-insight-constants';
import { getSummaryForSelectedDayAndExtractDate } from 'lumen/utils/market-insight/market-insight-helpers';
import parseNumber from 'lumen/utils/numeric/parse-number';

export default class MiSearchesService extends Service {
  @service intl;
  @service store;

  @tracked supportedSearchTypesForDestination = null;

  get areFlightBusinessSearchesSupportedForTheCurrentDestination() {
    return (
      this.supportedSearchTypesForDestination &&
      this.supportedSearchTypesForDestination.indexOf(
        MI_SEARCHES_TYPE_FLIGHTS_BUSINESS,
      ) !== -1
    );
  }

  get areFlightLeisureSearchesSupportedForTheCurrentDestination() {
    return (
      this.supportedSearchTypesForDestination &&
      this.supportedSearchTypesForDestination.indexOf(
        MI_SEARCHES_TYPE_FLIGHTS_LEISURE,
      ) !== -1
    );
  }

  get areHotelBusinessSearchesSupportedForTheCurrentDestination() {
    return (
      this.supportedSearchTypesForDestination &&
      this.supportedSearchTypesForDestination.indexOf(
        MI_SEARCHES_TYPE_HOTELS_BUSINESS,
      ) !== -1
    );
  }

  get areHotelLeisureSearchesSupportedForTheCurrentDestination() {
    return (
      this.supportedSearchTypesForDestination &&
      this.supportedSearchTypesForDestination.indexOf(
        MI_SEARCHES_TYPE_HOTELS_LEISURE,
      ) !== -1
    );
  }

  // Will be called when leaving the route/switching hotels
  clearValues() {
    this.supportedSearchTypesForDestination = null;
  }

  /**
   * Compose a correct search summary id with the passed parameters
   * @param {number} searchSummaryType allows us to select the correct search summary (flights or hotels)
   * @param {string} dayKey YYYY-MM-DD format
   * @param {string} extractDate can be latest or an actual extract date
   */
  composeSearchSummaryId(searchSummaryType, dayKey, extractDate) {
    let composedId = `${searchSummaryType},${dayKey}`;

    if (extractDate === FIXED_EXTRACT_DATE_LATEST) {
      composedId = composedId.concat(`,${FIXED_EXTRACT_DATE_LATEST}`);
    } else {
      const formattedExtractDate = extractDate.format('YYYY-MM-DD');
      composedId = composedId.concat(`,${formattedExtractDate}`);
    }

    return composedId;
  }

  /**
   * Allows to pass in a day object and an extract date. Will give back all country search objects for the correct extract date
   * @param {number} countrySearchType type of country search objects that need to be retrieved
   * @param {object} day day object
   * @param {dayjs/string} extractDate can be a string in case of latest, or dayjs object in the other case
   * @param {string} endDayRaw string representing the last day of the range
   */
  filterCountrySearchesByExtractDate(
    countrySearchType,
    day,
    extractDate,
    endDayRaw,
  ) {
    let searchModels = [];

    if (day && day.countrysearches) {
      searchModels = day.countrysearches;
    }

    return this.getSearches(
      searchModels,
      countrySearchType,
      day,
      extractDate,
      endDayRaw,
    );
  }

  /**
   * Allows to pass in a day object and an extract date. Will give back all LOS search objects for the correct extract date
   * @param {number} losSearchType type of los search objects that need to be retrieved
   * @param {object} day day object
   * @param {dayjs/string} extractDate can be a string in case of latest, or dayjs object in the other case
   * @param {string} endDayRaw string representing the last day of the range
   */
  filterLosSearchesByExtractDate(losSearchType, day, extractDate, endDayRaw) {
    return this.getSearches(
      day.lossearches,
      losSearchType,
      day,
      extractDate,
      endDayRaw,
    );
  }

  /**
   * Generate an array of all days between 2 given dayjs objects (in YYYY-MM-DD format)
   * Start and end day are included as well
   * @param { dayjs } startMoment
   * @param { dayjs } endMoment
   */
  generateDaysBetweenTwoMomentObjects(startMoment, endMoment) {
    const result = [];

    let loopMoment = startMoment;

    while (loopMoment.isSameOrBefore(endMoment, 'day')) {
      result.push(loopMoment.format('YYYY-MM-DD'));
      loopMoment = loopMoment.add(1, 'day');
    }

    return result;
  }

  getSearches(searchModels, type, day, extractDate, endDayRaw) {
    // The endDay is only used for the overview. If it's not present it should be the same as the day
    const endDay = endDayRaw || day.id;

    if (searchModels.length && extractDate) {
      const isLatest = extractDate === FIXED_EXTRACT_DATE_LATEST;
      // If it's latest, extractDate is already a string
      const formattedExtractDate = isLatest
        ? extractDate
        : extractDate.format('YYYY-MM-DD');
      // Need to filter by type first, to make sure we only take the correct search data objects into account
      return searchModels.filter((searchObject) => {
        const isCorrectType = searchObject.type === type;
        const isCorrectRange =
          searchObject.endDay.format('YYYY-MM-DD') === endDay;
        const isCorrectExtractDate = isLatest
          ? searchObject.isLatest
          : searchObject.extractDate.format('YYYY-MM-DD') ===
            formattedExtractDate;

        return isCorrectType && isCorrectRange && isCorrectExtractDate;
      });
    }

    return [];
  }

  getSearchSummaryForCurrentSelection(
    searchType,
    isDetailLoading,
    isCalendarLoading,
    selectedDayModel,
    currentExtractDate,
  ) {
    if (!isDetailLoading && !isCalendarLoading && selectedDayModel) {
      const selectedDay = selectedDayModel.date.format('YYYY-MM-DD');
      return getSummaryForSelectedDayAndExtractDate(
        this.store,
        'searchsummary',
        `${searchType},`,
        selectedDay,
        currentExtractDate,
      );
    }

    if (isDetailLoading && !isCalendarLoading && selectedDayModel) {
      // We already have the mostRecentDailySummary property on the day model. If the detail request is loading, we can show this.
      // This prevents the card values from disappearing when switching selected days.
      const mostRecentSearchSummary =
        selectedDayModel.getMostRecentSearchSummary(true, searchType);

      if (mostRecentSearchSummary) {
        return mostRecentSearchSummary;
      }
    }

    return null;
  }

  searchesTooltipText(
    flightsLeisureSearchSummary,
    flightsBusinessSearchSummary,
    hotelsLeisureSearchSummary,
    hotelsBusinessSearchSummary,
  ) {
    const flightSearches =
      parseNumber(flightsLeisureSearchSummary?.searchVolume) +
      parseNumber(flightsBusinessSearchSummary?.searchVolume);
    const hotelSearches =
      parseNumber(hotelsLeisureSearchSummary?.searchVolume) +
      parseNumber(hotelsBusinessSearchSummary?.searchVolume);

    return this.intl.t(
      'market_insight.searches.total_number_of_searches.tooltip',
      {
        totalNumberSearches: this.intl.formatNumber(
          flightSearches + hotelSearches,
        ),
      },
    );
  }

  setSupportedSearchTypesForCurrentDestination(supportedSearchTypes) {
    this.supportedSearchTypesForDestination = supportedSearchTypes;
  }
}
